import { useState, useEffect, useCallback, useRef } from 'react';
import { RecipeResponse } from '@/types';
import { favoriteService } from '@/services/favoriteService';
import { useAuth } from '@/contexts/AuthContext';

export const useFavorites = () => {
    const [favorites, setFavorites] = useState<RecipeResponse[]>([]);
    const [favoriteIds, setFavoriteIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { token, isLoggedIn } = useAuth();

    // 👇 Nuevo: bandera global dentro del hook (no del componente)
    const hasLoadedRef = useRef(false);

    const loadFavorites = useCallback(async () => {
        if (!isLoggedIn || !token) {
            console.log('🛑 No logged in or no token, skipping favorites load');
            setFavorites([]);
            setFavoriteIds([]);
            return;
        }

        try {
            console.log('🔄 Loading favorites...');
            setLoading(true);
            setError(null);

            const [favoritesData, idsData] = await Promise.all([
                favoriteService.getFavorites(token),
                favoriteService.getFavoriteIds(token)
            ]);

            console.log('✅ Favorites loaded:', favoritesData.length, 'recipes,', idsData.length, 'ids');
            setFavorites(favoritesData);
            setFavoriteIds(idsData);
        } catch (err: any) {
            console.error('❌ Error loading favorites:', err);
            setError(err.message || 'Error loading favorites');
        } finally {
            setLoading(false);
        }
    }, [isLoggedIn, token]);

    const addFavorite = async (recipeId: number): Promise<boolean> => {
        if (!isLoggedIn || !token) {
            console.log('🛑 Cannot add favorite - not logged in');
            return false;
        }

        try {
            console.log('➕ Adding favorite:', recipeId);
            await favoriteService.addToFavorites(recipeId, token);
            setFavoriteIds(prev => [...prev, recipeId]);
            await loadFavorites();
            return true;
        } catch (err: any) {
            console.error('❌ Error adding favorite:', err);
            await loadFavorites();
            return false;
        }
    };

    const removeFavorite = async (recipeId: number): Promise<boolean> => {
        if (!isLoggedIn || !token) {
            console.log('🛑 Cannot remove favorite - not logged in');
            return false;
        }

        try {
            console.log('➖ Removing favorite:', recipeId);
            await favoriteService.removeFromFavorites(recipeId, token);
            setFavoriteIds(prev => prev.filter(id => id !== recipeId));
            await loadFavorites();
            return true;
        } catch (err: any) {
            console.error('❌ Error removing favorite:', err);
            await loadFavorites();
            return false;
        }
    };

    const isFavorite = (recipeId: number): boolean => {
        return favoriteIds.includes(recipeId);
    };

    const toggleFavorite = async (recipeId: number): Promise<boolean> => {
        console.log('🔄 Toggling favorite for recipe:', recipeId);
        if (isFavorite(recipeId)) {
            return await removeFavorite(recipeId);
        } else {
            return await addFavorite(recipeId);
        }
    };

    useEffect(() => {
        console.log('🎯 useFavorites useEffect triggered', { isLoggedIn, hasToken: !!token });

        // 👇 Nuevo: si ya se cargó una vez, no volver a hacerlo
        if (hasLoadedRef.current) {
            console.log('⏭️ Skipping favorites load (already loaded)');
            return;
        }

        if (isLoggedIn && token) {
            loadFavorites();
            hasLoadedRef.current = true; // ✅ marca como cargado
        }
    }, [isLoggedIn, token, loadFavorites]);

    return {
        favorites,
        favoriteIds,
        loading,
        error,
        isFavorite,
        addFavorite,
        removeFavorite,
        toggleFavorite,
        refetch: loadFavorites
    };
};
